import { useTranslation } from "react-i18next"
import { API_ROUTES } from "../../configuration/RoutesApi"
import { useError } from "../../context/useErrorContext"
import { useLoad } from "../../context/useLoadContext"
import {
  DeleteRequest,
  DownloadFileRequest,
  GetRequest,
  PostFormRequest,
  PostRequest,
  PutRequest,
} from "../../services/ApiClientService"
import { EFiscalEnvironment } from "../../Objects/FiscalIntegration/EFiscalEnvironment"
import { EFiscalTaxRegime } from "../../Objects/FiscalIntegration/EFiscalTaxRegime"
import { type IFiscalIntegrationSettings } from "../../Objects/FiscalIntegration/IFiscalIntegrationSettings"
import { type ISaveFiscalIntegrationSettings } from "../../Objects/FiscalIntegration/ISaveFiscalIntegrationSettings"
import { type ITestFiscalConnection } from "../../Objects/FiscalIntegration/ITestFiscalConnection"
import { type IFiscalProductProfile } from "../../Objects/FiscalIntegration/IFiscalProductProfile"
import { type IFiscalDocument } from "../../Objects/FiscalIntegration/IFiscalDocument"
import {
  type IFiscalIssuerSettings,
  type ISaveFiscalIssuerSettings,
} from "../../Objects/FiscalIntegration/IFiscalIssuerSettings"
import { type IFiscalOrderEmissionStatus } from "../../Objects/FiscalIntegration/IFiscalOrderEmissionStatus"
import {
  type FiscalDocumentSearchResult,
  type IFiscalDocumentFilter,
} from "../../Objects/FiscalIntegration/IFiscalDocumentFilter"
import { type EPaymentType } from "../../Enum/EPaymentType"
import {
  type FiscalImportedDocumentSearchResult,
  type IFiscalImportedDocument,
  type IFiscalImportedDocumentFilter,
} from "../../Objects/FiscalIntegration/IFiscalImportedDocument"

type FiscalRequestFeedbackOptions = {
  showGlobalLoading?: boolean
  showGlobalError?: boolean
}

export const defaultFiscalIntegrationSettings: IFiscalIntegrationSettings = {
  isEnabled: false,
  issueAutomatically: false,
  printAutomatically: false,
  environment: EFiscalEnvironment.Homologation,
  taxRegime: EFiscalTaxRegime.SimpleNational,
  hasHomologationToken: false,
  hasProductionToken: false,
  webhookUrl: "",
  affiliateUrl: "https://focusnfe.com.br/",
  issuer: {
    cnpj: "",
    legalName: "",
    tradeName: "",
    stateRegistration: "",
    street: "",
    number: "",
    complement: "",
    neighborhood: "",
    city: "",
    state: "",
    zipCode: "",
    isConfigured: false,
  },
}

export const useFiscalIntegration = () => {
  const { t } = useTranslation("general")

  const { enableLoad, disableLoad } = useLoad()

  const { setError } = useError()

  const run = async <T,>(
    operation: () => Promise<{ data?: T }>,
    fallback: T,
    options: FiscalRequestFeedbackOptions = {},
  ): Promise<T> => {
    const { showGlobalLoading = true, showGlobalError = true } = options

    if (showGlobalLoading) {
      enableLoad()
    }

    try {
      const response = await operation()
      return response.data ?? fallback
    } catch (error: unknown) {
      if (showGlobalError) {
        const message = error instanceof Error ? error.message : t("label_unknown_error_description")
        setError(t("label_title_erro"), message)
      }

      throw error
    } finally {
      if (showGlobalLoading) {
        disableLoad()
      }
    }
  }

  const getSettings = () =>
    run(
      () => GetRequest<IFiscalIntegrationSettings>(API_ROUTES.FISCAL_INTEGRATION.SETTINGS),
      defaultFiscalIntegrationSettings,
    )

  const saveSettings = (settings: ISaveFiscalIntegrationSettings) =>
    run(() => PutRequest<IFiscalIntegrationSettings>(API_ROUTES.FISCAL_INTEGRATION.SETTINGS, settings), settings)

  const saveIssuerSettings = (issuer: ISaveFiscalIssuerSettings) =>
    run<IFiscalIssuerSettings>(
      () => PutRequest<IFiscalIssuerSettings>(API_ROUTES.FISCAL_INTEGRATION.ISSUER_SETTINGS, issuer),
      { ...issuer, isConfigured: false },
    )

  const testConnection = (request: ITestFiscalConnection) =>
    run(() => PostRequest<{ message: string }>(API_ROUTES.FISCAL_INTEGRATION.TEST_CONNECTION, request), { message: "" })

  const getProducts = () => run(() => GetRequest<IFiscalProductProfile[]>(API_ROUTES.FISCAL_INTEGRATION.PRODUCTS), [])

  const saveProduct = (product: IFiscalProductProfile) =>
    run(
      () =>
        PutRequest<IFiscalProductProfile>(API_ROUTES.FISCAL_INTEGRATION.PRODUCT(product.genericFoodEntityId), product),
      product,
    )

  const saveProductsBulk = (productIds: number[], profile: IFiscalProductProfile) =>
    run(
      () =>
        PutRequest<IFiscalProductProfile[]>(API_ROUTES.FISCAL_INTEGRATION.PRODUCTS_BULK, {
          genericFoodEntityIds: productIds,
          profile,
        }),
      [],
    )

  const getDocuments = (showFeedback = true) =>
    run(() => GetRequest<IFiscalDocument[]>(API_ROUTES.FISCAL_INTEGRATION.DOCUMENTS), [], {
      showGlobalLoading: showFeedback,
      showGlobalError: showFeedback,
    })

  const searchDocuments = (filter: IFiscalDocumentFilter) => {
    const params = new URLSearchParams({
      pageNumber: filter.pageNumber.toString(),
      pageSize: filter.pageSize.toString(),
    })

    if (filter.searchTerm.trim()) {
      params.set("searchTerm", filter.searchTerm.trim())
    }

    if (filter.status !== null) {
      params.set("status", filter.status.toString())
    }

    if (filter.dateStart) {
      params.set("dateStart", filter.dateStart)
    }

    if (filter.dateEnd) {
      params.set("dateEnd", filter.dateEnd)
    }

    return run<FiscalDocumentSearchResult>(
      () =>
        GetRequest<FiscalDocumentSearchResult>(
          `${API_ROUTES.FISCAL_INTEGRATION.SEARCH_DOCUMENTS}?${params.toString()}`,
        ),
      {
        items: [],
        pageNumber: filter.pageNumber,
        pageSize: filter.pageSize,
        totalCount: 0,
        hasNextPage: false,
      },
    )
  }

  const getOrderEmissionStatus = (orderId: number, showFeedback = true) =>
    run<IFiscalOrderEmissionStatus>(
      () => GetRequest<IFiscalOrderEmissionStatus>(API_ROUTES.FISCAL_INTEGRATION.ORDER_EMISSION_STATUS(orderId)),
      {
        shouldDisplay: false,
        isIntegrationReady: false,
        canEmit: false,
        requiresPaymentMethod: false,
        document: null,
      },
      {
        showGlobalLoading: showFeedback,
        showGlobalError: showFeedback,
      },
    )

  const emitOrder = (orderId: number, consumerCpf?: string, paymentType?: EPaymentType) =>
    run<IFiscalDocument | null>(
      () =>
        PostRequest<IFiscalDocument>(API_ROUTES.FISCAL_INTEGRATION.EMIT_ORDER(orderId), {
          consumerCpf: consumerCpf?.trim() || null,
          paymentType: paymentType ?? null,
        }),
      null,
    )

  const retryDocument = (documentId: number) =>
    run<IFiscalDocument | null>(
      () => PostRequest<IFiscalDocument>(API_ROUTES.FISCAL_INTEGRATION.RETRY(documentId), {}),
      null,
    )

  const refreshDocument = (documentId: number) =>
    run<IFiscalDocument | null>(
      () => PostRequest<IFiscalDocument>(API_ROUTES.FISCAL_INTEGRATION.REFRESH(documentId), {}),
      null,
    )

  const cancelDocument = (documentId: number, reason: string) =>
    run<IFiscalDocument | null>(
      () => PostRequest<IFiscalDocument>(API_ROUTES.FISCAL_INTEGRATION.CANCEL(documentId), { reason }),
      null,
    )

  const openDanfce = (document: IFiscalDocument) => {
    if (!document.danfceUrl) {
      return
    }

    window.open(document.danfceUrl, "_blank", "noopener,noreferrer")
  }

  const downloadXml = async (document: IFiscalDocument) => {
    enableLoad()

    try {
      await DownloadFileRequest(
        API_ROUTES.FISCAL_INTEGRATION.XML(document.id),
        `nfce-${document.number ?? document.reference}.xml`,
      )
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
    } finally {
      disableLoad()
    }
  }

  const printDocument = (documentId: number, printerConfigurationId: number) =>
    run(
      () =>
        PostRequest<{ message: string }>(
          API_ROUTES.FISCAL_INTEGRATION.PRINT(documentId, printerConfigurationId),
          {},
        ),
      { message: "" },
    )

  const buildImportedDocumentsParams = (filter: IFiscalImportedDocumentFilter, includePagination = true) => {
    const params = new URLSearchParams()

    if (includePagination) {
      params.set("pageNumber", filter.pageNumber.toString())
      params.set("pageSize", filter.pageSize.toString())
    }

    if (filter.year !== null) {
      params.set("year", filter.year.toString())
    }

    if (filter.month !== null) {
      params.set("month", filter.month.toString())
    }

    if (filter.searchTerm.trim()) {
      params.set("searchTerm", filter.searchTerm.trim())
    }

    if (filter.documentType !== null) {
      params.set("documentType", filter.documentType.toString())
    }

    return params
  }

  const searchImportedDocuments = (filter: IFiscalImportedDocumentFilter) =>
    run<FiscalImportedDocumentSearchResult>(
      () =>
        GetRequest<FiscalImportedDocumentSearchResult>(
          `${API_ROUTES.FISCAL_INTEGRATION.IMPORTED_DOCUMENTS}?${buildImportedDocumentsParams(filter).toString()}`,
        ),
      {
        items: [],
        pageNumber: filter.pageNumber,
        pageSize: filter.pageSize,
        totalCount: 0,
        totalSizeBytes: 0,
        availableYears: [],
        hasNextPage: false,
      },
    )

  const importDocuments = (files: File[], referenceYear: number, referenceMonth: number) =>
    run<IFiscalImportedDocument[]>(
      () =>
        PostFormRequest<IFiscalImportedDocument[]>(API_ROUTES.FISCAL_INTEGRATION.IMPORTED_DOCUMENTS, {
          Files: files,
          ReferenceYear: referenceYear,
          ReferenceMonth: referenceMonth,
        }),
      [],
    )

  const downloadImportedDocument = async (document: IFiscalImportedDocument) => {
    enableLoad()

    try {
      await DownloadFileRequest(
        API_ROUTES.FISCAL_INTEGRATION.IMPORTED_DOCUMENT_DOWNLOAD(document.id),
        document.fileName,
      )
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw error
    } finally {
      disableLoad()
    }
  }

  const deleteImportedDocument = (documentId: number) =>
    run<{ documentId: number }>(
      () => DeleteRequest<{ documentId: number }>(API_ROUTES.FISCAL_INTEGRATION.IMPORTED_DOCUMENT_DELETE(documentId)),
      { documentId },
    )

  const exportImportedDocuments = async (filter: IFiscalImportedDocumentFilter) => {
    enableLoad()

    try {
      const params = buildImportedDocumentsParams(filter, false)
      const period = filter.year === null ? "" : `-${filter.year}${filter.month === null ? "" : `-${filter.month}`}`
      await DownloadFileRequest(
        `${API_ROUTES.FISCAL_INTEGRATION.IMPORTED_DOCUMENT_EXPORT}?${params.toString()}`,
        `notas-fiscais-importadas${period}.zip`,
      )
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : t("label_unknown_error_description")
      setError(t("label_title_erro"), message)
      throw error
    } finally {
      disableLoad()
    }
  }

  return {
    getSettings,
    saveSettings,
    saveIssuerSettings,
    testConnection,
    getProducts,
    saveProduct,
    saveProductsBulk,
    getDocuments,
    searchDocuments,
    getOrderEmissionStatus,
    emitOrder,
    retryDocument,
    refreshDocument,
    cancelDocument,
    openDanfce,
    downloadXml,
    printDocument,
    searchImportedDocuments,
    importDocuments,
    downloadImportedDocument,
    deleteImportedDocument,
    exportImportedDocuments,
  }
}
